// The shape a medication order takes on the wire, and how many units it comes to.
//
// Lives in src/utils because three modules order medication — nurse-dashboard's
// PlaceOrderModal, doctor-console-ipd's OrderManagementDrawer, and the IPD
// care-plan modals. It was previously nurse-dashboard/orderQuantity.js and only
// the nurse path used it, which is why the two doctor paths each grew their own
// copy of the arithmetic and each got it slightly differently wrong.
//
// add_ipd_prescription/ stores details VERBATIM (manage_prescription.py:
// row.details) and hands the same object to generate_medication_schedule. So
// this object is not a display payload — it is what the ward actually gets
// scheduled off, and what MedicationReturnableView derives on-hand stock from.
//
// null is a first-class answer meaning "not computable" — an as-needed course
// has no total, and a missing duration must not become a guess. The UI renders
// that as "Stock unknown". Never return 0 to mean unknown: 0 is a real
// quantity, and conflating them would show an empty bin for an unbounded order.

const DAYS_PER_UNIT = {
  "Day(s)": 1,
  "Week(s)": 7,
  "Month(s)": 30,
  "Year(s)": 365,
};

// null for anything that is not a real number. Note the blank guard: Number("")
// and Number(null) are both 0, and a blank duration reported as 0 would look
// like a deliberate zero rather than an unanswered field.
const num = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// Rounds the ward runs, in order. These carry HOW MUCH per round — the "1-0-1"
// a nurse writes on a chart. A frequency alone only implies them, so without
// these fields the scheduler defaults every round to 1 and "two tablets in the
// morning, one at night" cannot be ordered at all.
export const SLOT_FIELDS = [
  { key: "morning", label: "M" },
  { key: "afternoon", label: "A" },
  { key: "evening", label: "E" },
  { key: "night", label: "N" },
];

const SLOTS = SLOT_FIELDS.map(({ key }) => key);

// Which rounds each frequency implies. Mirrors SLOT_FREQUENCY_MAP in
// main/ipd/medication_order.py — keep the two in step. A frequency absent from
// this map (STAT, SOS, the Q*H cycles, Weekly, Monthly) is not round-based:
// the scheduler ignores slot doses for those, so we must not collect them.
export const FREQUENCY_SLOTS = {
  OD: ["morning"],
  BD: ["morning", "night"],
  TDS: ["morning", "afternoon", "night"],
  QID: ["morning", "afternoon", "evening", "night"],
  HS: ["night"],
};

export const slotsApplyTo = (frequency) => Boolean(FREQUENCY_SLOTS[frequency]);

// Fixed-interval cycles: doses per day is 24 / the gap.
const INTERVAL_HOURS = { Q4H: 4, Q6H: 6, Q8H: 8, Q12H: 12 };

// Cycles longer than a day: one dose every N days, rounded UP so a part-cycle
// still ships the dose that falls in it (8 days Weekly = 2 doses, not 1.14).
const CYCLE_DAYS = { Weekly: 7, Monthly: 30 };

// As-needed. There is no schedule behind it, so the quantity is a DISPENSING
// ESTIMATE rather than arithmetic: one dose per day of the course. Stated here
// rather than buried so it can be changed in one place if the ward wants a
// fixed PRN pack instead.
const AS_NEEDED_DOSES_PER_DAY = 1;

/** Duration expressed in days, or null when it cannot be determined. */
export function durationInDays(duration, durationUnit) {
  const n = num(duration);
  if (n === null || n <= 0) return null;
  // An order measured in hours still occupies whole days of dispensing.
  if (durationUnit === "Hour(s)") return Math.max(1, Math.ceil(n / 24));
  const multiplier = DAYS_PER_UNIT[durationUnit];
  // An unrecognised unit is not silently treated as days — that would ship a
  // wrong quantity rather than an honest "unknown".
  if (!multiplier) return null;
  return n * multiplier;
}

/** Total units an order comes to, or null when it is not computable. */
export function totalQuantity(details) {
  const frequency = details?.frequency;
  if (!frequency) return null;

  // A STAT order is exactly one dose; its duration is irrelevant.
  if (frequency === "STAT") return 1;

  const days = durationInDays(details.duration, details.duration_unit);
  if (days === null) return null;

  // Interval cycles are checked BEFORE the round-based branch, because
  // generate_medication_schedule returns on its HOURLY_FREQUENCIES check
  // without ever reading slot doses. A Q6H order with M/A/E/N filled in is
  // scheduled by the interval, so the quantity must follow the interval too.
  const hours = INTERVAL_HOURS[frequency];
  if (hours) return (24 / hours) * days;

  if (slotsApplyTo(frequency)) {
    const perDay = SLOTS.reduce((sum, key) => sum + (num(details[key]) || 0), 0);
    if (perDay <= 0) return null;
    return perDay * days;
  }

  // Weekly / Monthly: one dose per cycle, part-cycles rounded up.
  const cycle = CYCLE_DAYS[frequency];
  if (cycle) return Math.ceil(days / cycle);

  // SOS / PRN — an estimate, not a schedule. See AS_NEEDED_DOSES_PER_DAY.
  if (frequency === "SOS") return AS_NEEDED_DOSES_PER_DAY * days;

  // A frequency we do not model. A guess is worse than "unknown".
  return null;
}

/**
 * The ONE `details` object add_ipd_prescription/ should ever receive.
 *
 * duration MUST stay numeric with duration_unit alongside it: the backend runs
 * compute_duration_days(details.duration, details.duration_unit), and a
 * flattened "5 Day(s)" string fails float() there and silently collapses the
 * whole order to a single day.
 *
 * `dose` is carried through untouched — the pharmacy screen and the backend's
 * medicine_details() both read it for display, so the structured fields are
 * additive rather than a replacement.
 *
 * Accepts either naming convention for the duration unit (`duration_unit` from
 * the care-plan forms, `durationUnit` from the CPOE cart).
 */
export function buildMedicationDetails(med = {}) {
  const durationUnit = med.duration_unit || med.durationUnit || "";
  const frequency = med.frequency || "";
  const duration = num(med.duration);

  const details = {
    frequency,
    duration,
    duration_unit: durationUnit,
    route: String(med.route || "").trim(),
    instructions: String(med.instructions || "").trim(),
    dose: String(med.dose || "").trim(),
    // Per-round doses. The scheduler prefers these over the frequency map, so
    // they are the thing that actually gets scheduled — omitted entirely for
    // frequencies where rounds don't apply, since sending them there would be
    // silently ignored.
    ...(slotsApplyTo(frequency)
      ? Object.fromEntries(SLOT_FIELDS.map(({ key }) => [key, Number(med[key]) || 0]))
      : {}),
  };

  // How many units this order comes to. MedicationReturnableView derives the
  // ward's on-hand stock from it; without it `returnable` is null and the ward
  // sees "Stock unknown". Omitted rather than zeroed when the regimen has no
  // computable total (as-needed, weekly).
  const quantity = totalQuantity(details);
  if (quantity !== null) details.total_quantity = quantity;

  return details;
}
